/**
 * Shared personal recent directory searches — keeps sidebar and /search in sync.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  addDirectoryRecentSearch,
  clearDirectoryRecentSearches,
  DIRECTORY_RECENT_SEARCHES_CHANGED,
  loadDirectoryRecentSearches,
  removeDirectoryRecentSearch,
  subscribeDirectoryRecentSearches,
} from '../services/directoryRecentSearches';

export function useDirectoryRecentSearches() {
  const [recents, setRecents] = useState<string[]>(() => loadDirectoryRecentSearches());

  useEffect(() => {
    const unsub = subscribeDirectoryRecentSearches(setRecents);
    const onDomChanged = (event: Event) => {
      const detail = (event as Event & { detail?: unknown }).detail;
      setRecents(Array.isArray(detail) ? (detail as string[]) : loadDirectoryRecentSearches());
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === 'adieuu.directory-search.recent') {
        setRecents(loadDirectoryRecentSearches());
      }
    };
    window.addEventListener(DIRECTORY_RECENT_SEARCHES_CHANGED, onDomChanged);
    window.addEventListener('storage', onStorage);
    return () => {
      unsub();
      window.removeEventListener(DIRECTORY_RECENT_SEARCHES_CHANGED, onDomChanged);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const add = useCallback((query: string) => {
    setRecents(addDirectoryRecentSearch(query));
  }, []);

  const remove = useCallback((query: string) => {
    setRecents(removeDirectoryRecentSearch(query));
  }, []);

  const clear = useCallback(() => {
    setRecents(clearDirectoryRecentSearches());
  }, []);

  return { recents, add, remove, clear };
}
